import { bootstrapApplication } from '@angular/platform-browser';
import {
  utmCampaignFromUrl,
  utmMediumFromUrl,
  utmSourceFromUrl,
} from '@common';
import {
  detectVariantFromPath,
  DOMAIN_CONFIG,
  DomainConfig,
  DomainId,
  domainIdFromDomain,
  Environment,
  RUNTIME_CONFIG,
  RuntimeConfig,
} from '@config';
import { environment } from 'src/environments/environment';
import { AppComponent } from './app/components/app/app.component';
import { BUILD_CONFIGS } from './app/configs/build-configs.constant';
import { VARIANT_CONFIGS } from './app/configs/variant-configs.constant';
import { provideApp } from './app/providers/app.provider';

export async function bootstrap() {
  try {
    const variant = detectVariantFromPath();
    const buildConfig = BUILD_CONFIGS[variant];
    const variantConfig = VARIANT_CONFIGS[variant];

    const resolvedEnvironment = {
      ...buildConfig,
      name: environment.name,
      production: environment.production,
    };

    const [domainConfig, runtimeConfig] = await Promise.all([
      loadDomainConfig(resolvedEnvironment),
      loadRuntimeConfig(resolvedEnvironment),
    ]);

    bootstrapApplication(AppComponent, {
      providers: [
        { provide: DOMAIN_CONFIG, useValue: domainConfig },
        { provide: RUNTIME_CONFIG, useValue: runtimeConfig },
        provideApp(variantConfig, resolvedEnvironment),
      ],
    }).catch((error) => console.error(error));
  } catch (error) {
    console.error(error);
  }
}

async function loadDomainConfig(env: Environment): Promise<DomainConfig> {
  const domainId: DomainId = domainIdFromDomain(
    env.defaultDomainId,
    env.domainIdMapping,
  );

  const domainConfigPath = `configs/${env.appVariantConfigPath}/domains/${domainId}.json`;
  const utmCampaign = utmCampaignFromUrl();
  const utmMedium = utmMediumFromUrl();
  const utmSource = utmSourceFromUrl();

  if (!utmCampaign || !utmMedium || !utmSource) {
    const response = await fetch(domainConfigPath);
    return response.json();
  }

  const subDomainConfigPath =
    domainId !== utmSource.toLowerCase() && utmMedium === 'app'
      ? `configs/${env.appVariantConfigPath}/domains/${domainId}-${utmSource.toLowerCase()}.json`
      : domainConfigPath;

  try {
    const response = await fetch(subDomainConfigPath);
    if (!response.ok) throw new Error('Subdomain config not found');
    return response.json();
  } catch {
    const response = await fetch(domainConfigPath);
    return response.json();
  }
}

async function loadRuntimeConfig(env: Environment): Promise<RuntimeConfig> {
  const response = await fetch(
    `configs/${env.appVariantConfigPath}/deployments/config.${env.name}.json`,
  );
  return response.json();
}
